import { useState } from 'react';
import {
  Home,
  Heart,
  MessageCircle,
  Users,
  PlayCircle,
  Package,
  LogOut,
  Menu,
  X,
  Droplets,
} from 'lucide-react';

const NAV_ITEMS = [
  { page: 'dashboard', label: 'Dashboard', icon: Home },
  { page: 'health', label: 'Health', icon: Heart },
  { page: 'ai-assistant', label: 'AI Assistant', icon: MessageCircle },
  { page: 'community-challenges', label: 'Community', icon: Users },
  { page: 'manage-package', label: 'Subscription', icon: Package },
  { page: 'videos', label: 'Videos', icon: PlayCircle },
] as const;

interface AppTopNavProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  userEmail?: string | null;
  onSignOut: () => void;
  /** Optional: no longer shown in nav; edit profile is on Profile page */
  onSettingsClick?: () => void;
}

export function AppTopNav({
  currentPage,
  onNavigate,
  userEmail,
  onSignOut,
}: AppTopNavProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (page: string) => currentPage === page;

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <button
            onClick={() => onNavigate('dashboard')}
            className="flex items-center space-x-2 shrink-0"
            aria-label="Go to Dashboard"
          >
            <div className="p-1.5 bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-lg shadow-md">
              <Droplets className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <span className="font-bold text-lg md:text-xl bg-gradient-to-r from-dropsugar-blue to-dropsugar-blue-dark bg-clip-text text-transparent hidden sm:block">
              DropSugar
            </span>
          </button>

          {/* Main nav links - desktop */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map(({ page, label, icon: Icon }) => {
              const active = isActive(page);
              return (
                <button
                  key={page}
                  onClick={() => onNavigate(page)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-dropsugar-green/10 text-dropsugar-green'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              );
            })}
          </div>

          {/* Right: Packages, Sign out - desktop (Settings/Edit is on Profile page) */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => onNavigate('packages')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-dropsugar-green hover:bg-dropsugar-green/10 rounded-lg text-sm font-medium transition-colors"
              title="Packages"
            >
              <Package className="w-4 h-4" />
              <span>Packages</span>
            </button>
            <button
              onClick={onSignOut}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>

          {/* Mobile: menu button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile dropdown */}
        {menuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100 space-y-1">
            {NAV_ITEMS.map(({ page, label, icon: Icon }) => {
              const active = isActive(page);
              return (
                <button
                  key={page}
                  onClick={() => {
                    onNavigate(page);
                    setMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left text-sm font-medium transition-colors ${
                    active ? 'bg-dropsugar-green/10 text-dropsugar-green' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </button>
              );
            })}
            <div className="border-t border-gray-100 pt-2 mt-2 space-y-1">
              {userEmail && (
                <p className="px-4 py-2 text-xs text-gray-500 truncate">{userEmail}</p>
              )}
              <button
                onClick={() => {
                  onNavigate('packages');
                  setMenuOpen(false);
                }}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Package className="w-5 h-5" />
                Packages
              </button>
              <button
                onClick={() => {
                  onSignOut();
                  setMenuOpen(false);
                }}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
